
import { computed, defineComponent, onBeforeMount, PropType, reactive } from 'vue'
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import { companies } from '@/core/realisations/companies.realisation'
import { contracts } from '@/core/realisations/contracts.realisation'
import { DataState } from '@/core/classes/DataState.class'
import { CompanyT } from '@/core/types/companies.types'
import { ContractRegister, ContractService, DocumentTypeT } from '@/core/types/contracts.types'
import useVuelidate from '@vuelidate/core'
import { required, requiredIf, integer, minValue } from '@vuelidate/validators'
import useHelpers from '@/common/composable/useHelpers'
import useNotifications from '@/common/composable/useNotifications'
import { useMaska } from '@/common/composable/useMaska'
import { PRICE_MASK } from '@/core/constants/masks.constants'
import { TARIFF_TYPES, PRICE_CHANGE_TYPES } from '@/core/constants/contracts.constants'

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        TheSelect,
        TheInput,
        FileReader,
        Checkbox,
        Loader,
    },
    props: {
        contractId: {
            type: Number,
            default: null,
        },
        contract: {
            type: Object as PropType<ContractRegister>,
            required: true,
        },
    },
    emits: ['cancel', 'added'],
    setup(props, { emit }) {
        const notifications = useNotifications()
        const { unmaskNumbers } = useMaska()

        const state = reactive({
            is_loading: false,
            type_id: null as number | null,
            company_id: null as number | null,
            subdivision_id: null as number | null,
            subdivision_name: null as string | null,
            name: null as string | null,
            bin: null as string | null,
            number: null as string | null,
            date: null as string | null,
            files: [] as File[],
            description: null as string | null,
            tariff_id: null as number | null,
            price_change_id: null as number | null,
            amount: null as number | null,
            expiration_date: null as string | null,
            notice_before: null as string | null,
            is_auto_renewal: 0 as number,
            serviceIds: [] as number[],
        })

        const state_form$ = useVuelidate(
            {
                company_id: { required },
                name: { required },
                bin: { required },
                number: { required },
                date: { required },
                type_id: { required },
                expiration_date: { required },
                subdivision_id: { required },
                description: { required: requiredIf(() => state.type_id === 2) },
                tariff_id: { required: requiredIf(() => state.type_id === 2) },
                price_change_id: { required: requiredIf(() => state.type_id === 2) },
                notice_before: {
                    required: requiredIf(() => state.price_change_id === 3),
                    integer,
                    minValue: minValue(1),
                },
                serviceIds: { required },
            },
            state
        )

        const data_state = reactive({
            subdivisions: new DataState<any>({}),
            companies: new DataState<CompanyT>({}),
            types: new DataState<DocumentTypeT>({}),
            services: new DataState<ContractService>({}),
        })

        const loadSubdivisions = async () => {
            try {
                data_state.subdivisions.is_loading = true
                const response = await companies.getSubdivisions({ page: 1 })
                data_state.subdivisions.list = response.data

                data_state.subdivisions.is_failed = false
                data_state.subdivisions.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.subdivisions.failure.catchError(error)

                if (!data_state.subdivisions.failure.error_state.is_cancel) {
                    data_state.subdivisions.is_failed = true
                }
            } finally {
                if (!data_state.subdivisions.failure.error_state.is_cancel) {
                    data_state.subdivisions.is_loading = false
                }
            }
        }

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({
                    page: 1,
                    getAll: 1,
                    limit: 9999,
                    isExtended: 0,
                    search,
                })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({ page, isExtended: 0 })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCompanies = (search_query: string) => {
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        const loadTypes = async (page: number) => {
            data_state.types.pagination.current_page = page

            try {
                data_state.types.is_loading = true

                const response = await contracts.getTypes({ page, isMain: 1 })
                data_state.types.list = response.data
                data_state.types.pagination.total_pages = response.meta.last_page

                data_state.types.is_failed = false
                data_state.types.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.types.failure.catchError(error)

                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_failed = true
                }
            } finally {
                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_loading = false
                }
            }
        }

        const loadMoreTypes = async (page: number) => {
            if (page > data_state.types.pagination.total_pages) return
            data_state.types.pagination.current_page = page

            try {
                data_state.types.is_loading = true

                const response = await contracts.getTypes({ page, isMain: 1 })
                data_state.types.list = data_state.types.list.concat(response.data)

                data_state.types.pagination.total_pages = response.meta.last_page

                data_state.types.is_failed = false
                data_state.types.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.types.failure.catchError(error)

                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_failed = true
                }
            } finally {
                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_loading = false
                }
            }
        }

        const emitCancel = () => {
            emit('cancel')
        }

        const fillInfo = () => {
            if (!props.contract) return

            state.company_id = props.contract.companyId
            state.subdivision_id = props.contract.subdivisionId
            // state.serviceIds = props.contract.serviceIds.map((item) => item.id)
            state.name = props.contract.companyName
            state.bin = props.contract.companyBin
            state.number = props.contract.number
            if (props.contract.type) state.type_id = Number(props.contract.type.id)

            if (state.subdivision_id) onSubdivisionSelected(state.subdivision_id)
            if (state.company_id) onCompanySelected(state.company_id)
        }

        const getToastMessage = (type: string) => {
            if (type === 'success') {
                return 'Договор успешно добавлен!'
            } else {
                return 'При добавлении договора произошла ошибка'
            }
        }

        const addContract = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (
                !state.name ||
                !state.type_id ||
                !state.bin ||
                !state.number ||
                !state.date ||
                !state.expiration_date ||
                !state.subdivision_id ||
                !state.serviceIds.length ||
                !state.subdivision_name ||
                !props.contractId
            )
                return

            try {
                state.is_loading = true
                const form_data = new FormData()

                form_data.append('name', state.name)
                form_data.append('type_id', state.type_id.toString())
                form_data.append('bin', state.bin)
                form_data.append('number', state.number)
                form_data.append('date', state.date.toString())
                form_data.append('expiration_date', state.expiration_date.toString())
                form_data.append('subdivision_id', state.subdivision_id.toString())
                form_data.append('subdivision_name', state.subdivision_name)
                form_data.append('is_auto_renewal', state.is_auto_renewal.toString())
                form_data.append('status_id', '1')
                form_data.append('registerId', props.contractId.toString())
                form_data.append('serviceIds', JSON.stringify(state.serviceIds))

                if (state.description) {
                    form_data.append('description', state.description as string)
                }
                if (state.company_id) {
                    form_data.append('company_id', state.company_id.toString())
                }
                if (state.tariff_id) {
                    form_data.append('tariff_id', state.tariff_id.toString())
                }
                if (state.price_change_id) {
                    form_data.append('price_change_id', state.price_change_id.toString())
                }
                if (state.amount) {
                    form_data.append('amount', unmaskNumbers(state.amount.toString()))
                }
                if (state.notice_before) {
                    form_data.append('notice_before', state.notice_before)
                }
                if (state.files.length) form_data.append('file', state.files[0])

                await contracts.createContract(form_data)

                notifications.notify({ title: getToastMessage('success'), type: 'success' })
                emit('added')
            } catch (error: any) {
                console.error(error)
                if (!error.length) {
                    notifications.notify({ title: 'Произошла ошибка!', type: 'danger' })
                } else
                    notifications.notifyError({
                        error,
                        title: getToastMessage('error'),
                    })
            } finally {
                state.is_loading = false
            }
        }

        const onCompanySelected = (company_id: number) => {
            const { findObjectByValue } = useHelpers()
            const company = findObjectByValue('id', company_id, data_state.companies.list)

            if (!company) return

            state.name = company.name
            state.bin = company.bin
        }

        const onSubdivisionSelected = (subdivision_id: number) => {
            const { findObjectByValue } = useHelpers()
            const subdivision = findObjectByValue(
                'id',
                subdivision_id,
                data_state.subdivisions.list
            )

            if (!subdivision) return

            state.subdivision_name = subdivision.name
        }

        const resetForm = () => {
            state.type_id = null
            state.company_id = null
            state.name = null
            state.bin = null
            state.number = null
            state.date = null
            state.files = []
            state.expiration_date = null
            state.notice_before = null
            state.is_auto_renewal = 0

            state_form$.value.$reset()
        }

        const onTypeChange = (type_id: any) => {
            if (!type_id || type_id !== 2) {
                state.description = null
                state.tariff_id = null
                state.price_change_id = null
                state.notice_before = null
                state.amount = null
            }
        }

        const onPriceChange = (price_id: any) => {
            if (!price_id || price_id !== 3) {
                state.notice_before = null
            }
        }
        const isLoading = computed(() => {
            return (
                data_state.types.is_loading ||
                data_state.companies.is_loading ||
                data_state.subdivisions.is_loading
            )
        })

        const loadServices = async () => {
            try {
                data_state.services.is_loading = true
                const response = await contracts.getServices()
                data_state.services.list = response.data

                data_state.services.is_failed = false
                data_state.services.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.services.failure.catchError(error)

                if (!data_state.services.failure.error_state.is_cancel) {
                    data_state.services.is_failed = true
                }
            } finally {
                if (!data_state.services.failure.error_state.is_cancel) {
                    data_state.services.is_loading = false
                }
            }
        }

        onBeforeMount(async () => {
            state.is_loading = true

            loadServices()
            loadTypes(1)
            await loadCompanies(1)
            await loadSubdivisions()

            if (props.contract) fillInfo()

            state.is_loading = false
        })

        return {
            state,
            state_form$,
            data_state,
            emitCancel,
            loadSubdivisions,
            onSubdivisionSelected,
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFindedCompany,
            loadTypes,
            loadMoreTypes,
            onCompanySelected,
            resetForm,
            onTypeChange,
            onPriceChange,
            addContract,
            loadServices,
            isLoading,
            TARIFF_TYPES,
            PRICE_CHANGE_TYPES,
            PRICE_MASK,
        }
    },
})
